import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import EntryDetail from "../components/EntryDetail";
import DeleteEntryModal from "../components/DeleteEntryModal";
import { useSettings } from "../context/SettingsContext";
import client from "../api/client";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function EntryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(null);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const entryRef = useRef(null);
  const [guidedSession, setGuidedSession] = useState(null);

  useEffect(() => {
    const fetchEntry = async () => {
      try {
        const res = await client.get(`/entries/${id}`);
        setEntry(res.data);

        // If this entry has a linked session flow, fetch it
        if (res.data.sitter_session_id) {
          try {
            //const sessionRes = await client.get(`/entries/${id}/session`);
            const sessionRes = await client.get(`/entries/${id}/sitter-session`);
            if (sessionRes.data.session) setGuidedSession(sessionRes.data.session);
          } catch {
            // Non-fatal — session panel simply won't render
          }
        }
      } catch (err) {
        setError("Unable to load entry.");
      } finally {
        setLoading(false);
      }
    };
    fetchEntry();
  }, [id]);


  const handleDelete = async () => {
  try {
    const res = await client.get(`/entries/${id}/session-info`);
    setDeleteModal({ isRoot: res.data.isRoot, childCount: res.data.childCount });
  } catch {
    alert("Failed to load entry info.");
  }
};

const confirmDelete = async (deleteAll = false) => {
  setDeleteModal(null);
  try {
    await client.delete(`/entries/${id}${deleteAll ? "?deleteAll=true" : ""}`);
    navigate("/");
  } catch {
    alert("Failed to delete entry.");
  }
};

  const handleExportPDF = async () => {
    if (!entryRef.current) return;
    setExporting(true);
    try {
      const el = entryRef.current;

      // Add PDF header
      const header = document.createElement("div");
      header.id = "pdf-header";
      header.innerHTML = `<div style="font-family: 'Comfortaa', cursive; font-size: 22px; font-weight: 400; color: #000000; letter-spacing: 1px; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px solid #dddddd; text-align: right;">Journey Trails</div>`;
      el.insertBefore(header, el.firstChild);

      // Inject a temporary print stylesheet
      const style = document.createElement("style");
      style.id = "pdf-export-style";
      style.innerHTML = `
        [data-pdf-capture] * {
          color: #1a1a1a !important;
          background: #ffffff !important;
          border-color: #dddddd !important;
        }
        [data-pdf-capture] h1,
        [data-pdf-capture] h2,
        [data-pdf-capture] h3 {
          color: #000000 !important;
        }
        [data-pdf-capture] .EntryDetail article,
        [data-pdf-capture] article {
          color: #000000 !important;
        }
      `;
      document.head.appendChild(style);
      el.setAttribute("data-pdf-capture", "true");

      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Cleanup
      el.removeAttribute("data-pdf-capture");
      document.getElementById("pdf-export-style")?.remove();
      document.getElementById("pdf-header")?.remove();

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const usableWidth = pageWidth - margin * 2;
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const usableHeight = pageHeight - margin * 2;

      let yOffset = 0;
      while (yOffset < imgHeight) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(
          imgData, "PNG",
          margin,
          margin - (yOffset * pageWidth) / imgWidth,
          imgWidth,
          imgHeight
        );
        yOffset += usableHeight;
      }

      const filename = entry?.title
        ? `${entry.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.pdf`
        : `journey_entry_${id}.pdf`;

      pdf.save(filename);
    } catch (err) {
      console.error("PDF export failed:", err);
      entryRef.current?.removeAttribute("data-pdf-capture");
      document.getElementById("pdf-export-style")?.remove();
      document.getElementById("pdf-header")?.remove();
      alert("Failed to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <p>Loading entry…</p>;
  if (error) return <p>{error}</p>;
  if (!entry) return <p>Entry not found.</p>;

  return (
    <main className="EntryDetailPage">
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px"
      }}>
        <span
          onClick={() => navigate("/")}
          style={{
            cursor: "pointer",
            fontSize: "14px",
            opacity: 0.7,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px"
          }}
        >
         
        </span>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <span
            onClick={handleExportPDF}
            style={{
              cursor: exporting ? "default" : "pointer",
              fontSize: "14px",
              opacity: exporting ? 0.85 : 0.7,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            {exporting ? "Exporting…" : "↓ PDF"}
          </span>
          <span
            data-html2canvas-ignore="true"
            onClick={() => navigate(`/entry/${id}/edit`)}
            style={{
              cursor: "pointer",
              fontSize: "14px",
              opacity: 0.85,
              display: "inline-flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            ✎ Edit
          </span>
          <span
            onClick={handleDelete}
            data-html2canvas-ignore="true"
            style={{
              cursor: "pointer",
              fontSize: "14px",
              color: "#ffaa33",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px"
            }}
          >
            🗑 Delete
          </span>
        </div>
      </div>

      <div ref={entryRef}>
        <EntryDetail entry={entry} guidedSession={guidedSession} />
      </div>
      {deleteModal && (
              <DeleteEntryModal
                isRoot={deleteModal.isRoot}
                childCount={deleteModal.childCount}
                onDeleteAll={() => confirmDelete(true)}
                onDeleteRoot={() => confirmDelete(false)}
                onDeleteSingle={() => confirmDelete(false)}
                onCancel={() => setDeleteModal(null)}
              />
            )}
    </main>
  );
}

export default EntryDetailPage;
